import i18n from 'i18next'
import { initReactI18next } from 'react-i18next'
import LanguageDetector from 'i18next-browser-languagedetector'
import frCommon from '@/locales/fr/common.json'
import arCommon from '@/locales/ar/common.json'
import enCommon from '@/locales/en/common.json'
import frShell from '@/locales/fr/shell.json'
import arShell from '@/locales/ar/shell.json'
import enShell from '@/locales/en/shell.json'
import frSources from '@/locales/fr/sources.json'
import arSources from '@/locales/ar/sources.json'
import enSources from '@/locales/en/sources.json'
import frCase from '@/locales/fr/case.json'
import arCase from '@/locales/ar/case.json'
import enCase from '@/locales/en/case.json'
import frPlan from '@/locales/fr/plan.json'
import arPlan from '@/locales/ar/plan.json'
import enPlan from '@/locales/en/plan.json'
import frAgent from '@/locales/fr/agent.json'
import arAgent from '@/locales/ar/agent.json'
import enAgent from '@/locales/en/agent.json'
import frVersions from '@/locales/fr/versions.json'
import arVersions from '@/locales/ar/versions.json'
import enVersions from '@/locales/en/versions.json'
import frComments from '@/locales/fr/comments.json'
import arComments from '@/locales/ar/comments.json'
import enComments from '@/locales/en/comments.json'

const isDev = import.meta.env.DEV

/**
 * Per-surface namespace merge (F-FOUND §2.4). The add-in diverges from the
 * FE single flat `fr.json`: each surface ships its OWN namespace file
 * (`common.json`, future `sources.json`, …) under `src/locales/{fr,ar,en}/`
 * so parallel rebuild sessions never collide on a shared JSON. F-FOUND ships
 * only the `common` namespace; later features append their own.
 *
 * The files are flat-merged under a single i18next `translation` resource
 * (keyed by their namespace prefix) so the dotted key shape `common.*` /
 * `shell.*` / `sources.*` stays stable — NOT registered as separate i18next
 * namespaces. This is the FROZEN resource shape (§8 contract).
 */
function buildTranslation(
  common: Record<string, unknown>,
  shell: Record<string, unknown>,
  sources: Record<string, unknown>,
  caseNs: Record<string, unknown>,
  plan: Record<string, unknown>,
  agent: Record<string, unknown>,
  versions: Record<string, unknown>,
  comments: Record<string, unknown>,
): {
  common: Record<string, unknown>
  shell: Record<string, unknown>
  sources: Record<string, unknown>
  case: Record<string, unknown>
  plan: Record<string, unknown>
  agent: Record<string, unknown>
  versions: Record<string, unknown>
  comments: Record<string, unknown>
} {
  return { common, shell, sources, case: caseNs, plan, agent, versions, comments }
}

/**
 * ``i18next`` invokes ``missingKeyHandler`` BEFORE checking whether the
 * caller passed a ``defaultValue``. The "probe" pattern (BE-payload-driven
 * call sites) passes ``defaultValue: ''`` precisely because the key is
 * expected to miss in some branches — without this guard those probes spam
 * ``console.warn`` once per miss.
 *
 * Logic: if the caller passed ANY ``defaultValue`` (including the empty
 * string), skip the warning — the call site signalled it knows the key
 * might be missing. Otherwise (genuine missing key, oversight), warn loudly
 * in dev. Production silently no-ops via the outer ``!isDev`` check.
 *
 * Exported for unit testing — production wiring in ``i18n.init`` below binds
 * it directly. Note ``options`` is typed ``unknown`` because i18next's
 * runtime ``options`` bag is heterogeneous and a partial-shape check is
 * safer than pretending we know all keys.
 */
export function shouldWarnMissingKey(options: unknown): boolean {
  if (!isDev) return false
  if (options && typeof options === 'object') {
    const opts = options as { defaultValue?: unknown }
    if (opts.defaultValue !== undefined) return false
  }
  return true
}

i18n
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    resources: {
      fr: {
        translation: buildTranslation(
          frCommon,
          frShell,
          frSources,
          frCase,
          frPlan,
          frAgent,
          frVersions,
          frComments,
        ),
      },
      ar: {
        translation: buildTranslation(
          arCommon,
          arShell,
          arSources,
          arCase,
          arPlan,
          arAgent,
          arVersions,
          arComments,
        ),
      },
      en: {
        translation: buildTranslation(
          enCommon,
          enShell,
          enSources,
          enCase,
          enPlan,
          enAgent,
          enVersions,
          enComments,
        ),
      },
    },
    // Only these three exact codes are supported. ``en-US`` (a common
    // WebView default) is normalised by ``load: 'languageOnly'`` to ``en``
    // so the language pill, ``html[lang]``, and the resource bundle stay in
    // sync.
    supportedLngs: ['fr', 'ar', 'en'],
    load: 'languageOnly',
    fallbackLng: 'fr',
    interpolation: { escapeValue: false },
    detection: {
      // No Office.js seam (W2): i18n.ts is a `lib/` side-effect and stays
      // Office-agnostic. `navigator.language` inside the WebView reflects
      // the host locale closely enough; the explicit toggle (persisted to
      // localStorage) is the authoritative override. The deferred
      // `Office.context.displayLanguage` seed would live behind
      // `adapters/office/` (F-FOUND §2.1 / §5).
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
    },
    saveMissing: isDev,
    missingKeyHandler: (lngs, _ns, key, _fallback, _updateMissing, options) => {
      if (!shouldWarnMissingKey(options)) return
      // eslint-disable-next-line no-console
      console.warn(`[i18n] missing key "${key}" for ${lngs.join(',')}`)
    },
  })

// Set document direction based on language (mirrors FE).
i18n.on('languageChanged', (lng) => {
  const dir = lng === 'ar' ? 'rtl' : 'ltr'
  document.documentElement.dir = dir
  document.documentElement.lang = lng
})

// N1 smoke harness (F-FOUND §4 note + §7): a `?lang=` URL override read
// once at init so the founder can sideload `?lang=ar` and confirm `dir`
// propagates inside the Word WebView. Dev/smoke aid — `localStorage` +
// `navigator` remain the authoritative detection chain otherwise.
if (typeof window !== 'undefined') {
  const override = new URLSearchParams(window.location.search).get('lang')
  if (override && ['fr', 'ar', 'en'].includes(override)) {
    void i18n.changeLanguage(override)
  }
}

export default i18n
